import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import LoadingSpinner from './LoadingSpinner';

const SongDuplicateResolutionModal = ({ 
  isOpen, 
  onClose, 
  duplicateData,
  onMerge,
  onCreateNew,
  onSkip
}) => {
  const [selectedAction, setSelectedAction] = useState('merge');
  const [selectedDuplicate, setSelectedDuplicate] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  React.useEffect(() => {
    if (duplicateData?.duplicates?.length > 0) {
      setSelectedDuplicate(duplicateData?.duplicates?.[0]);
    }
  }, [duplicateData]);

  const handleAction = async () => {
    if (!duplicateData) return;

    setIsProcessing(true);
    
    try {
      switch (selectedAction) {
        case 'merge':
          if (selectedDuplicate) {
            await onMerge(duplicateData?.originalData, selectedDuplicate?.song);
          }
          break;
        case 'create':
          await onCreateNew(duplicateData?.originalData);
          break;
        case 'skip':
          onSkip?.();
          break;
      }
      onClose();
    } catch (error) {
      console.error('Error handling duplicate resolution:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen || !duplicateData) return null;

  const { duplicates, originalData, confidence } = duplicateData;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Icon name="Copy" size={20} className="text-amber-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Duplicate Songs Found
              </h3>
              <p className="text-sm text-gray-600">
                AI detected {duplicates?.length} potential duplicate(s) for "{originalData?.title}"
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <Icon name="X" size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Confidence Score */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="Brain" size={16} className="text-blue-600" />
              <span className="text-sm font-medium text-blue-900">
                AI Confidence: {Math.round(confidence * 100)}%
              </span>
            </div>
          </div>

          {/* New Song Info */}
          <div className="mb-6">
            <h4 className="text-md font-semibold text-gray-900 mb-3">New Song Information</h4>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <span className="text-sm font-medium text-green-900">Title:</span>
                  <p className="text-sm text-green-800">{originalData?.title}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-green-900">Musical:</span>
                  <p className="text-sm text-green-800">{originalData?.musicalTitle || 'Not specified'}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-green-900">Act:</span>
                  <p className="text-sm text-green-800">Act {originalData?.actNumber || 1}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-green-900">Level 1:</span>
                  <p className="text-sm text-green-800 truncate">{originalData?.level1 || 'Not provided'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Existing Duplicates */}
          <div className="mb-6">
            <h4 className="text-md font-semibold text-gray-900 mb-3">Existing Songs Found</h4>
            <div className="space-y-3">
              {duplicates?.map((duplicate, index) => (
                <div 
                  key={index}
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                    selectedDuplicate === duplicate
                      ? 'border-indigo-500 bg-indigo-50' :'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedDuplicate(duplicate)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <span className="text-sm font-medium text-gray-900">Title:</span>
                          <p className="text-sm text-gray-700">{duplicate?.title}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-900">Musical:</span>
                          <p className="text-sm text-gray-700">{duplicate?.musical || 'Not specified'}</p>
                        </div>
                      </div>
                      <div className="mt-2">
                        <span className="text-xs font-medium text-gray-600">Reason:</span>
                        <p className="text-xs text-gray-600">{duplicate?.reason}</p>
                      </div>
                    </div>
                    <div className="ml-4 flex items-center">
                      <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded">
                        {Math.round(duplicate?.similarity * 100)}% match
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Action Selection */}
          <div className="mb-6">
            <h4 className="text-md font-semibold text-gray-900 mb-3">Choose Action</h4>
            <div className="space-y-3">
              <label className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="radio"
                  name="action"
                  value="merge"
                  checked={selectedAction === 'merge'}
                  onChange={(e) => setSelectedAction(e?.target?.value)}
                  className="mt-1"
                />
                <div>
                  <div className="flex items-center space-x-2">
                    <Icon name="GitMerge" size={16} className="text-indigo-600" />
                    <span className="font-medium text-gray-900">Merge with Existing Song</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Update the selected existing song with new information from the .pages file
                  </p>
                </div>
              </label>

              <label className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="radio"
                  name="action"
                  value="create"
                  checked={selectedAction === 'create'}
                  onChange={(e) => setSelectedAction(e?.target?.value)}
                  className="mt-1"
                />
                <div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Plus" size={16} className="text-green-600" />
                    <span className="font-medium text-gray-900">Create as New Song</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Import as a new song despite similarities (useful for different versions)
                  </p>
                </div>
              </label>

              <label className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="radio"
                  name="action"
                  value="skip"
                  checked={selectedAction === 'skip'}
                  onChange={(e) => setSelectedAction(e?.target?.value)}
                  className="mt-1"
                />
                <div>
                  <div className="flex items-center space-x-2">
                    <Icon name="X" size={16} className="text-gray-600" />
                    <span className="font-medium text-gray-900">Skip Import</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Don't import this song and cancel the process
                  </p>
                </div>
              </label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleAction}
            disabled={isProcessing || (selectedAction === 'merge' && !selectedDuplicate)}
            iconName={selectedAction === 'merge' ? 'GitMerge' : selectedAction === 'create' ? 'Plus' : 'X'}
            iconPosition="left"
          >
            {isProcessing ? (
              <LoadingSpinner size="sm" />
            ) : (
              selectedAction === 'merge' ? 'Merge Songs' :
              selectedAction === 'create' ? 'Create New' : 'Skip Import'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SongDuplicateResolutionModal;